import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Enhanced validation with more specific error messages
if (!supabaseUrl) {
  throw new Error('VITE_SUPABASE_URL is not defined in environment variables. Please check your .env file.');
}

if (!supabaseAnonKey) {
  throw new Error('VITE_SUPABASE_ANON_KEY is not defined in environment variables. Please check your .env file.');
}

// Additional validation to ensure the key is not empty or placeholder
if (supabaseAnonKey.trim() === '' || supabaseAnonKey === 'your-anon-key-here' || supabaseAnonKey === 'placeholder') {
  throw new Error('VITE_SUPABASE_ANON_KEY appears to be empty or a placeholder. Please set it to your actual Supabase anonymous key.');
}

// Validate URL format
try {
  new URL(supabaseUrl);
} catch (error) {
  throw new Error(`VITE_SUPABASE_URL is not a valid URL: ${supabaseUrl}. Please check your .env file.`);
}

// Validate that the URL looks like a Supabase URL
if (!supabaseUrl.includes('supabase.co') && !supabaseUrl.includes('localhost')) {
  console.warn('Warning: VITE_SUPABASE_URL does not appear to be a standard Supabase URL. Please verify it is correct.');
}

// Debug logging for development
if (import.meta.env.DEV) {
  console.log('Supabase URL:', supabaseUrl);
  console.log('Supabase Anon Key (first 10 chars):', supabaseAnonKey.substring(0, 10) + '...');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

// Test connection function
export const testConnection = async () => {
  try {
    const { data, error } = await supabase.from('documents').select('count', { count: 'exact', head: true });
    if (error && error.code !== 'PGRST116') { // PGRST116 is "relation does not exist" which is fine for testing connection
      throw error;
    }
    return { success: true };
  } catch (error: any) {
    console.error('Supabase connection test failed:', error);
    return { success: false, error: error.message };
  }
};

// Auth helper functions with enhanced error handling
export const authHelpers = {
  signUp: async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`
        }
      });
      return { data, error };
    } catch (error: any) {
      console.error('SignUp network error:', error);
      if (error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to authentication service. Please check your internet connection and try again.');
      }
      throw error;
    }
  },

  signIn: async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { data, error };
    } catch (error: any) {
      console.error('SignIn network error:', error);
      if (error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to authentication service. Please check your internet connection and try again.');
      }
      throw error;
    }
  },

  signOut: async () => {
    try {
      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (error: any) {
      console.error('SignOut network error:', error);
      if (error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to authentication service. Please check your internet connection and try again.');
      }
      throw error;
    }
  },

  getCurrentUser: async () => {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      return { user, error };
    } catch (error: any) {
      console.error('GetCurrentUser network error:', error);
      if (error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to authentication service. Please check your internet connection and try again.');
      }
      throw error;
    }
  },

  getSession: async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      return { session, error };
    } catch (error: any) {
      console.error('GetSession network error:', error);
      if (error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to authentication service. Please check your internet connection and try again.');
      }
      throw error;
    }
  },

  resetPassword: async (email: string) => {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth`
      });
      return { data, error };
    } catch (error: any) {
      console.error('ResetPassword network error:', error);
      if (error.message === 'Failed to fetch') {
        throw new Error('Unable to connect to authentication service. Please check your internet connection and try again.');
      }
      throw error;
    }
  }
};